// client/src/components/react-dashboard/store-management/ProductForm.jsx

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline,
  ArrowBackOutlined,
  DeleteOutline,
  ImageOutlined,
  OndemandVideoOutlined,
  SaveOutlined,
} from '@mui/icons-material';

import {
  BILLING_TYPES,
  CATEGORIES,
  FITMENT_OPTIONS,
  STATUSES,
  normalizeImageUrl,
  slugify,
} from './productUtils';

const ProductForm = ({
  colors,
  product,
  setProduct,
  selectedFitment,
  setSelectedFitment,
  selectedFitmentCount,
  isEditing,
  isSaving,
  isProductToEditFetching,
  isUploadingImage,
  uploadProductImage,
  updateField,
  updateNestedField,
  handleNameChange,
  handleCancelForm,
  handleSaveProduct,
  setNotice,
}) => {
  const handleImageSelect = async (event) => {
    const files = Array.from(event.target.files || []);

    event.target.value = '';

    if (!files.length) return;

    try {
      setNotice(null);

      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];

        const formData = new FormData();
        formData.append('image', file);
        formData.append('alt', product.name || file.name);
        formData.append('sortOrder', String(product.images.length + index));
        formData.append('isPrimary', String(product.images.length === 0 && index === 0));

        const response = await uploadProductImage(formData).unwrap();

        setProduct((prev) => ({
          ...prev,
          images: [
            ...prev.images,
            {
              ...response.image,
              isPrimary: prev.images.length === 0,
              sortOrder: prev.images.length,
            },
          ],
        }));
      }

      setNotice({
        severity: 'success',
        message: `${files.length} image${files.length === 1 ? '' : 's'} uploaded successfully.`,
      });
    } catch (error) {
      setNotice({
        severity: 'error',
        message: error?.data?.message || error?.message || 'Image upload failed.',
      });
    }
  };

  const toggleFitment = (atgModel) => {
    setSelectedFitment((prev) => ({
      ...prev,
      [atgModel]: !prev[atgModel],
    }));
  };

  const removeUploadedImage = (indexToRemove) => {
    setProduct((prev) => ({
      ...prev,
      images: prev.images
        .filter((_, index) => index !== indexToRemove)
        .map((image, index) => ({
          ...image,
          isPrimary: index === 0,
          sortOrder: index,
        })),
    }));
  };

  const setPrimaryUploadedImage = (indexToSet) => {
    setProduct((prev) => ({
      ...prev,
      images: prev.images.map((image, index) => ({
        ...image,
        isPrimary: index === indexToSet,
        sortOrder: index,
      })),
    }));
  };

  const updateVideo = (index, field, value) => {
    setProduct((prev) => ({
      ...prev,
      videos: prev.videos.map((video, videoIndex) =>
        videoIndex === index
          ? {
            ...video,
            [field]: value,
          }
          : video
      ),
    }));
  };

  const addVideo = () => {
    setProduct((prev) => ({
      ...prev,
      videos: [
        ...prev.videos,
        {
          provider: 'youtube',
          title: '',
          description: '',
          url: '',
          embedUrl: '',
          youtubeId: '',
          thumbnailUrl: '',
          isPrimary: prev.videos.length === 0,
          sortOrder: prev.videos.length,
        },
      ],
    }));
  };

  const removeVideo = (indexToRemove) => {
    setProduct((prev) => ({
      ...prev,
      videos: prev.videos
        .filter((_, index) => index !== indexToRemove)
        .map((video, index) => ({
          ...video,
          isPrimary: index === 0,
          sortOrder: index,
        })),
    }));
  };

  const setPrimaryVideo = (indexToSet) => {
    setProduct((prev) => ({
      ...prev,
      videos: prev.videos.map((video, index) => ({
        ...video,
        isPrimary: index === indexToSet,
        sortOrder: index,
      })),
    }));
  };

  const updateFeature = (index, field, value) => {
    setProduct((prev) => ({
      ...prev,
      features: prev.features.map((feature, featureIndex) =>
        featureIndex === index
          ? {
            ...feature,
            [field]: value,
          }
          : feature
      ),
    }));
  };

  const addFeature = () => {
    setProduct((prev) => ({
      ...prev,
      features: [
        ...prev.features,
        {
          title: '',
          description: '',
          icon: '',
          sortOrder: prev.features.length + 1,
        },
      ],
    }));
  };

  const removeFeature = (indexToRemove) => {
    setProduct((prev) => ({
      ...prev,
      features: prev.features.filter((_, index) => index !== indexToRemove),
    }));
  };

  const updateSection = (index, field, value) => {
    setProduct((prev) => ({
      ...prev,
      sections: prev.sections.map((section, sectionIndex) =>
        sectionIndex === index
          ? {
            ...section,
            [field]: value,
          }
          : section
      ),
    }));
  };

  const addSection = () => {
    setProduct((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        {
          heading: '',
          body: '',
          bulletsText: '',
          sortOrder: prev.sections.length + 1,
        },
      ],
    }));
  };

  const removeSection = (indexToRemove) => {
    setProduct((prev) => ({
      ...prev,
      sections: prev.sections.filter((_, index) => index !== indexToRemove),
    }));
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackOutlined />}
          onClick={handleCancelForm}
        >
          Back to Products
        </Button>

        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={18} /> : <SaveOutlined />}
          onClick={handleSaveProduct}
          disabled={isSaving || isProductToEditFetching}
        >
          {isEditing ? 'Update Product' : 'Create Product'}
        </Button>
      </Box>

      {isProductToEditFetching && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading product details...
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} xl={8}>
          <Stack spacing={2}>
            <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Typography variant="h4" fontWeight="bold" mb={2}>
                Product Basics
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Product Name"
                    value={product.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Slug"
                    value={product.slug}
                    onChange={(e) => updateField('slug', slugify(e.target.value))}
                    helperText="Used in product URLs. Example: atg-pro"
                    required
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="SKU"
                    value={product.sku}
                    onChange={(e) => updateField('sku', e.target.value.toUpperCase())}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Category"
                    value={product.category}
                    onChange={(e) => updateField('category', e.target.value)}
                  >
                    {CATEGORIES.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    value={product.status}
                    onChange={(e) => updateField('status', e.target.value)}
                  >
                    {STATUSES.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Sort Order"
                    value={product.sortOrder}
                    onChange={(e) => updateField('sortOrder', Number(e.target.value))}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box height="100%" display="flex" alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={product.featured}
                          onChange={(e) => updateField('featured', e.target.checked)}
                        />
                      }
                      label="Featured Product"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tagline"
                    value={product.tagline}
                    onChange={(e) => updateField('tagline', e.target.value)}
                    placeholder="7” touchscreen + CPU/networking retrofit for legacy ATGs"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Short Description"
                    value={product.shortDescription}
                    onChange={(e) => updateField('shortDescription', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={5}
                    label="Full Description"
                    value={product.description}
                    onChange={(e) => updateField('description', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    Product Images
                  </Typography>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Images upload to s3://usfuel-web/site_pictures and save to this product.
                  </Typography>
                </Box>

                <Button
                  component="label"
                  variant="contained"
                  startIcon={isUploadingImage ? <CircularProgress size={18} /> : <ImageOutlined />}
                  disabled={isUploadingImage}
                >
                  {isUploadingImage ? 'Uploading...' : 'Upload Pictures'}
                  <input
                    hidden
                    multiple
                    accept="image/*"
                    type="file"
                    onChange={handleImageSelect}
                  />
                </Button>
              </Box>

              {product.images.length === 0 ? (
                <Box
                  border={`1px dashed ${colors.grey[500]}`}
                  borderRadius="8px"
                  p={4}
                  textAlign="center"
                >
                  <ImageOutlined sx={{ fontSize: 50, color: colors.grey[400] }} />
                  <Typography color={colors.grey[300]}>
                    Upload product photos. The first uploaded image becomes the primary image.
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {product.images.map((image, index) => {
                    const imageUrl = normalizeImageUrl(image);

                    return (
                      <Grid item xs={12} sm={6} md={4} key={`${image.s3Key || imageUrl}-${index}`}>
                        <Card sx={{ backgroundColor: colors.primary[500] }}>
                          <CardMedia
                            component="img"
                            height="180"
                            image={imageUrl}
                            alt={image.alt || product.name}
                            sx={{ objectFit: 'cover' }}
                          />

                          <CardContent>
                            <Typography variant="body2" noWrap>
                              {image.s3Key || imageUrl}
                            </Typography>

                            {image.isPrimary && (
                              <Chip
                                label="Primary"
                                size="small"
                                color="success"
                                sx={{ mt: 1 }}
                              />
                            )}
                          </CardContent>

                          <CardActions>
                            <Button size="small" onClick={() => setPrimaryUploadedImage(index)}>
                              Set Primary
                            </Button>

                            <IconButton color="error" onClick={() => removeUploadedImage(index)}>
                              <DeleteOutline />
                            </IconButton>
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Paper>

            <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    Product Videos
                  </Typography>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Optional YouTube video shown on the public product page.
                  </Typography>
                </Box>

                <Button startIcon={<AddCircleOutline />} onClick={addVideo}>
                  Add Video
                </Button>
              </Box>

              {product.videos.length === 0 ? (
                <Box
                  border={`1px dashed ${colors.grey[500]}`}
                  borderRadius="8px"
                  p={4}
                  textAlign="center"
                >
                  <OndemandVideoOutlined sx={{ fontSize: 50, color: colors.grey[400] }} />
                  <Typography color={colors.grey[300]}>
                    No videos added. Add a YouTube link if this product has a demo or overview video.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {product.videos.map((video, index) => (
                    <Paper key={index} sx={{ p: 2, backgroundColor: colors.primary[500] }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            select
                            label="Provider"
                            value={video.provider || 'youtube'}
                            onChange={(e) => updateVideo(index, 'provider', e.target.value)}
                          >
                            <MenuItem value="youtube">YouTube</MenuItem>
                            <MenuItem value="vimeo">Vimeo</MenuItem>
                            <MenuItem value="custom">Custom</MenuItem>
                          </TextField>
                        </Grid>

                        <Grid item xs={12} md={5}>
                          <TextField
                            fullWidth
                            label="Video Title"
                            value={video.title}
                            onChange={(e) => updateVideo(index, 'title', e.target.value)}
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Sort"
                            value={video.sortOrder}
                            onChange={(e) => updateVideo(index, 'sortOrder', Number(e.target.value))}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="YouTube URL or Video ID"
                            value={video.url}
                            onChange={(e) => updateVideo(index, 'url', e.target.value)}
                            placeholder="https://www.youtube.com/watch?v=..."
                            helperText="Paste a YouTube URL, youtu.be short link, Shorts link, embed URL, or the 11-character video ID."
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Description"
                            value={video.description}
                            onChange={(e) => updateVideo(index, 'description', e.target.value)}
                          />
                        </Grid>

                        <Grid item xs={12} md={8}>
                          <TextField
                            fullWidth
                            label="Thumbnail URL"
                            value={video.thumbnailUrl}
                            onChange={(e) => updateVideo(index, 'thumbnailUrl', e.target.value)}
                            helperText="Optional. YouTube thumbnail is auto-generated if left blank."
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Box display="flex" alignItems="center" gap={1} height="100%">
                            <Button
                              variant={video.isPrimary ? 'contained' : 'outlined'}
                              onClick={() => setPrimaryVideo(index)}
                            >
                              {video.isPrimary ? 'Primary' : 'Set Primary'}
                            </Button>

                            <IconButton color="error" onClick={() => removeVideo(index)}>
                              <DeleteOutline />
                            </IconButton>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Paper>

            <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Typography variant="h4" fontWeight="bold" mb={2}>
                Pricing
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Billing Type"
                    value={product.pricing.billingType}
                    onChange={(e) => updateNestedField('pricing', 'billingType', e.target.value)}
                  >
                    {BILLING_TYPES.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Currency"
                    value={product.pricing.currency}
                    onChange={(e) => updateNestedField('pricing', 'currency', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Display Price Text"
                    value={product.pricing.displayPriceText}
                    onChange={(e) => updateNestedField('pricing', 'displayPriceText', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Base Price"
                    value={product.pricing.basePrice}
                    onChange={(e) => updateNestedField('pricing', 'basePrice', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Monthly Price"
                    value={product.pricing.monthlyPrice}
                    onChange={(e) => updateNestedField('pricing', 'monthlyPrice', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Annual Price"
                    value={product.pricing.annualPrice}
                    onChange={(e) => updateNestedField('pricing', 'annualPrice', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Setup Fee"
                    value={product.pricing.setupFee}
                    onChange={(e) => updateNestedField('pricing', 'setupFee', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h4" fontWeight="bold">
                  ATG Fitment
                </Typography>

                <Chip label={`${selectedFitmentCount} selected`} color="success" />
              </Box>

              <Grid container spacing={1}>
                {FITMENT_OPTIONS.map((option) => (
                  <Grid item xs={12} sm={6} md={4} key={option}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(selectedFitment[option])}
                          onChange={() => toggleFitment(option)}
                        />
                      }
                      label={option}
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>

            <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4" fontWeight="bold">
                  Features
                </Typography>

                <Button startIcon={<AddCircleOutline />} onClick={addFeature}>
                  Add Feature
                </Button>
              </Box>

              <Stack spacing={2}>
                {product.features.map((feature, index) => (
                  <Paper key={index} sx={{ p: 2, backgroundColor: colors.primary[500] }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Feature Title"
                          value={feature.title}
                          onChange={(e) => updateFeature(index, 'title', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Icon Key"
                          value={feature.icon}
                          onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Sort"
                          value={feature.sortOrder}
                          onChange={(e) => updateFeature(index, 'sortOrder', Number(e.target.value))}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Description"
                          value={feature.description}
                          onChange={(e) => updateFeature(index, 'description', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} md={1}>
                        <IconButton color="error" onClick={() => removeFeature(index)}>
                          <DeleteOutline />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4" fontWeight="bold">
                  Product Page Sections
                </Typography>

                <Button startIcon={<AddCircleOutline />} onClick={addSection}>
                  Add Section
                </Button>
              </Box>

              <Stack spacing={2}>
                {product.sections.map((section, index) => (
                  <Paper key={index} sx={{ p: 2, backgroundColor: colors.primary[500] }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Section Heading"
                          value={section.heading}
                          onChange={(e) => updateSection(index, 'heading', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Sort"
                          value={section.sortOrder}
                          onChange={(e) => updateSection(index, 'sortOrder', Number(e.target.value))}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <IconButton color="error" onClick={() => removeSection(index)}>
                          <DeleteOutline />
                        </IconButton>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label="Body"
                          value={section.body}
                          onChange={(e) => updateSection(index, 'body', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          label="Bullets"
                          value={section.bulletsText}
                          onChange={(e) => updateSection(index, 'bulletsText', e.target.value)}
                          helperText="One bullet per line"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        <Grid item xs={12} xl={4}>
          <Stack spacing={2}>
            <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Typography variant="h4" fontWeight="bold" mb={2}>
                Included Items
              </Typography>

              <TextField
                fullWidth
                multiline
                minRows={6}
                label="Included Items"
                value={product.includedItemsText}
                onChange={(e) => updateField('includedItemsText', e.target.value)}
                helperText="One item per line"
              />
            </Paper>

            <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Typography variant="h4" fontWeight="bold" mb={2}>
                Technical Specs
              </Typography>

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Screen Size"
                  value={product.technicalSpecs.screenSize}
                  onChange={(e) => updateNestedField('technicalSpecs', 'screenSize', e.target.value)}
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Connectivity"
                  value={product.technicalSpecs.connectivityText}
                  onChange={(e) => updateNestedField('technicalSpecs', 'connectivityText', e.target.value)}
                  helperText="One value per line"
                />

                <TextField
                  fullWidth
                  label="Power Requirements"
                  value={product.technicalSpecs.powerRequirements}
                  onChange={(e) => updateNestedField('technicalSpecs', 'powerRequirements', e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Mounting"
                  value={product.technicalSpecs.mounting}
                  onChange={(e) => updateNestedField('technicalSpecs', 'mounting', e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Operating System"
                  value={product.technicalSpecs.operatingSystem}
                  onChange={(e) => updateNestedField('technicalSpecs', 'operatingSystem', e.target.value)}
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Supported Exports"
                  value={product.technicalSpecs.supportedExportsText}
                  onChange={(e) => updateNestedField('technicalSpecs', 'supportedExportsText', e.target.value)}
                  helperText="One value per line"
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Supported Connections"
                  value={product.technicalSpecs.supportedConnectionsText}
                  onChange={(e) => updateNestedField('technicalSpecs', 'supportedConnectionsText', e.target.value)}
                  helperText="One value per line"
                />

                <TextField
                  fullWidth
                  label="Warranty"
                  value={product.technicalSpecs.warranty}
                  onChange={(e) => updateNestedField('technicalSpecs', 'warranty', e.target.value)}
                />
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Typography variant="h4" fontWeight="bold" mb={2}>
                Inventory
              </Typography>

              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={product.inventory.trackInventory}
                      onChange={(e) => updateNestedField('inventory', 'trackInventory', e.target.checked)}
                    />
                  }
                  label="Track Inventory"
                />

                <TextField
                  fullWidth
                  type="number"
                  label="Quantity On Hand"
                  value={product.inventory.quantityOnHand}
                  onChange={(e) => updateNestedField('inventory', 'quantityOnHand', Number(e.target.value))}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={product.inventory.allowBackorder}
                      onChange={(e) => updateNestedField('inventory', 'allowBackorder', e.target.checked)}
                    />
                  }
                  label="Allow Backorder"
                />
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Typography variant="h4" fontWeight="bold" mb={2}>
                SEO
              </Typography>

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="SEO Title"
                  value={product.seo.title}
                  onChange={(e) => updateNestedField('seo', 'title', e.target.value)}
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="SEO Description"
                  value={product.seo.description}
                  onChange={(e) => updateNestedField('seo', 'description', e.target.value)}
                />

                <TextField
                  fullWidth
                  label="SEO Keywords"
                  value={product.seo.keywordsText}
                  onChange={(e) => updateNestedField('seo', 'keywordsText', e.target.value)}
                  helperText="Comma-separated"
                />
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Typography variant="h4" fontWeight="bold" mb={2}>
                Save Controls
              </Typography>

              <Stack spacing={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={isSaving ? <CircularProgress size={18} /> : <SaveOutlined />}
                  onClick={handleSaveProduct}
                  disabled={isSaving || isProductToEditFetching}
                >
                  {isEditing ? 'Update Product' : 'Create Product'}
                </Button>

                <Button fullWidth variant="outlined" onClick={handleCancelForm}>
                  Cancel
                </Button>

                <Divider />

                <Typography variant="body2" color={colors.grey[300]}>
                  Images upload to S3 immediately, then save to Mongo when you create or update the product.
                </Typography>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </>
  );
};

export default ProductForm;